import { SIM_HZ } from "@shared/sim/constants";
import {
  INPUT_REDUNDANCY,
  decodeServerMessage,
  encodeInputs,
  encodeJoin,
  encodePing,
  numToStance,
  type NetInput,
  type RemotePlayerQ,
  type Snapshot,
  type FileMsg, type SocialMsg, type MissionMsg, type RunMsg, type CityEventMsg, type CityRunMsg, type CityRosterMsg, encodeChoice, encodeTerminal, type TerminalMsg,
} from "@shared/net/protocol";
import { joinDelay, joinGiveUpMs } from "@shared/net/rejoin";
import type { Transport } from "./transport";

/** Remote players are rendered this many ticks behind the estimated server tick. */
export const INTERP_DELAY_TICKS = 6;

export interface RemoteView {
  id: number;
  name: string;
  /** identity tag (glyph seed, chapter, moniker, debt flag) — render only */
  tag: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  height: number;
  alive: boolean;
  stance: "stand" | "crouch" | "slide" | "mantle";
  health: number;
  /** the newest sample's velocity, footing and weapon slot (Stage 63): the body's pose reads them; the wire carried them already */
  vx: number;
  vy: number;
  vz: number;
  grounded: boolean;
  slot: number;
}

interface RemoteSample {
  tick: number;
  q: RemotePlayerQ;
}

/**
 * Client side of the protocol: join, send redundant input batches, receive
 * delta snapshots, estimate the server clock, and interpolate remote players.
 * Reconciliation of the local player is the Game's job (it owns the world).
 */
export class NetClient {
  playerId = -1;
  token: string;
  status: "connecting" | "joined" | "closed" | "kicked" = "connecting";
  kickReason = "";
  levelName = "";
  /** the room's mode from the Welcome: "", `audit:<id>:<week>`, or `campaign` */
  mode = "";
  /** Room seed from Welcome: magazine seeds derive from it on both sides. */
  seed = 1;
  rttMs = 0;
  private rttSamples: number[] = [];
  private seq = 0;
  private pending: NetInput[] = [];
  private baselines = new Map<number, Snapshot>();
  private latestTick = 0;
  private latestAt = 0;
  private remotes = new Map<number, RemoteSample[]>();
  private names = new Map<number, string>();
  private tags = new Map<number, string>();
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  readonly stats = { snapshots: 0, bytesIn: 0, bytesOut: 0, undecodable: 0, reconciles: 0, maxCorrection: 0, joinedAtMs: 0, connectStartMs: performance.now() };
  onSnapshot: ((s: Snapshot) => void) | null = null;
  onStatus: ((s: NetClient["status"]) => void) | null = null;
  onFile: ((f: FileMsg) => void) | null = null;
  onSocial: ((m: SocialMsg) => void) | null = null;
  onMission: ((m: MissionMsg) => void) | null = null;
  onRun: ((m: RunMsg) => void) | null = null;
  /** the city's public event (Stage 699): only a city room sends it */
  onCityEvent: ((m: CityEventMsg) => void) | null = null;
  /** the city's street runs (Stage 703): only a city room sends it */
  onCityRun: ((m: CityRunMsg) => void) | null = null;
  /**
   * Everyone in the room, names only, when the room says (Stage 706). A city room tells each client
   * only about the files near it, so its snapshots stop being the room; this is. Null in every room
   * that does not send it, where the snapshot's player list still is the room.
   */
  roster: CityRosterMsg["players"] | null = null;

  constructor(private transport: Transport, private name: string, token = "", private account = "", private loadout = "", private identity = "", private secret = "") {
    this.token = token;
    transport.onOpen = () => this.ask();
    transport.onMessage = (buf) => this.receive(buf);
    transport.onClose = (reason) => {
      if (this.status !== "kicked") this.status = "closed";
      this.kickReason ||= reason;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.disarmJoin();
      this.onStatus?.(this.status);
    };
  }

  /** how many times the join has been asked, the first ask included (Stage 155) */
  private asks = 0;
  private joinTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Ask to join, and arm the wait for the Welcome (Stage 155).
   *
   * A join and its Welcome are two packets on a link that loses them; neither used to be timed, so
   * one loss stranded the client in `connecting` for as long as the page was open. The ask repeats
   * on the shared plan, and when the plan is spent the link is closed so the knock takes the door —
   * a client that hears nothing at all is told so rather than left waiting.
   */
  private ask(): void {
    this.transport.send(encodeJoin(this.name, this.token, this.account, this.loadout, this.identity, this.secret));
    const wait = joinDelay(++this.asks);
    if (this.joinTimer) clearTimeout(this.joinTimer);
    this.joinTimer = setTimeout(() => {
      this.joinTimer = null;
      if (wait === null) this.transport.close();
      else this.ask();
    }, wait ?? joinGiveUpMs());
  }

  private disarmJoin(): void {
    if (this.joinTimer) clearTimeout(this.joinTimer);
    this.joinTimer = null;
  }

  /** how many asks it took to be let in, for the probe and the stats line */
  get joinAsks(): number {
    return this.asks;
  }

  /** Co-op: resolve a dialogue on the room (host only; the room ignores others). */
  sendChoice(script: string, testimony: Record<string, string>): void {
    this.transport.send(encodeChoice(script, testimony));
    this.stats.bytesOut += 8;
  }

  /** the host's terminal, node by node, for the crew to read (Stage 52) */
  sendTerminal(m: TerminalMsg): void {
    this.transport.send(encodeTerminal(m));
    this.stats.bytesOut += 8;
  }

  get pendingInputs(): readonly NetInput[] {
    return this.pending;
  }

  /** the server tick of the newest snapshot this client has applied: how far into the room's past it is */
  get snapshotTick(): number {
    return this.latestTick;
  }

  /** Estimated current server tick (continuous). */
  serverTickNow(): number {
    if (!this.latestTick) return 0;
    return this.latestTick + ((performance.now() - this.latestAt) / 1000) * SIM_HZ + (this.rttMs / 2 / 1000) * SIM_HZ;
  }

  /** Tick the remote players are rendered at (what the local player is aiming at). */
  viewTick(): number {
    return Math.max(0, Math.floor(this.serverTickNow() - INTERP_DELAY_TICKS));
  }

  /**
   * The part of the view time `viewTick()` throws away, in [0, 1) (Stage 34).
   *
   * `remoteViews()` poses remotes at the continuous `serverTickNow() - INTERP_DELAY_TICKS`; this is
   * the same quantity's fraction, so the pair names the exact instant the player aimed at and the
   * server can rewind to it rather than to the tick before it.
   */
  viewFrac(): number {
    const t = this.serverTickNow() - INTERP_DELAY_TICKS;
    return t <= 0 ? 0 : t - Math.floor(t);
  }

  nextSeq(): number {
    return ++this.seq;
  }

  /** Queue one input (already applied locally) and send the last few redundantly. */
  sendInput(input: NetInput): void {
    this.pending.push(input);
    if (this.pending.length > 240) this.pending.splice(0, this.pending.length - 240);
    const batch = this.pending.slice(-INPUT_REDUNDANCY);
    const buf = encodeInputs(batch, this.latestTick);
    this.stats.bytesOut += buf.byteLength;
    this.transport.send(buf);
  }

  /** Drop inputs the server has processed; returns the ones still to replay. */
  ackUpTo(seq: number): NetInput[] {
    let i = 0;
    while (i < this.pending.length && this.pending[i]!.seq <= seq) i++;
    this.pending.splice(0, i);
    return this.pending;
  }

  /** true once this client was closed on purpose, so a drop can be told from a departure (Stage 153) */
  left = false;

  close(): void {
    this.left = true;
    this.disarmJoin();
    this.transport.close();
  }

  private receive(buf: ArrayBuffer): void {
    this.stats.bytesIn += buf.byteLength;
    const msg = decodeServerMessage(buf, (t) => this.baselines.get(t) ?? null);
    if (!msg) {
      this.stats.undecodable++;
      return;
    }
    switch (msg.type) {
      case "file":
        this.onFile?.(msg.file);
        break;
      case "social":
        this.onSocial?.(msg.social);
        break;
      case "mission":
        this.onMission?.(msg.mission);
        break;
      case "run":
        this.onRun?.(msg.run);
        break;
      case "cityEvent":
        this.onCityEvent?.(msg.cityEvent);
        break;
      case "cityRun":
        this.onCityRun?.(msg.cityRun);
        break;
      case "cityRoster":
        this.roster = msg.cityRoster.players;
        break;
      case "welcome":
        // the room says hello again when it thinks the first one was lost (Stage 155); a client
        // already in its seat takes the repeat as an ack and nothing else — running the branch
        // twice would start a second ping timer and tell the game it had joined a second time
        this.disarmJoin();
        if (this.status === "joined") break;
        this.playerId = msg.playerId;
        this.token = msg.token;
        this.levelName = msg.level;
        this.seed = msg.seed;
        this.mode = msg.mode;
        this.status = "joined";
        this.stats.joinedAtMs = performance.now();
        this.latestTick = msg.tick;
        this.latestAt = performance.now();
        this.pingTimer = setInterval(() => this.transport.send(encodePing(performance.now() >>> 0)), 500);
        this.transport.send(encodePing(performance.now() >>> 0));
        this.onStatus?.(this.status);
        break;
      case "pong": {
        const rtt = (performance.now() >>> 0) - msg.clientTime;
        if (rtt >= 0 && rtt < 5000) {
          this.rttSamples.push(rtt);
          if (this.rttSamples.length > 8) this.rttSamples.shift();
          const sorted = [...this.rttSamples].sort((a, b) => a - b);
          this.rttMs = sorted[Math.floor(sorted.length / 2)]!;
        }
        break;
      }
      case "kick":
        this.status = "kicked";
        this.disarmJoin();
        this.kickReason = msg.reason;
        this.onStatus?.(this.status);
        break;
      case "snapshot": {
        const s = msg.snapshot;
        if (s.tick <= this.latestTick && this.stats.snapshots > 0) break; // stale/out of order
        this.stats.snapshots++;
        this.latestTick = s.tick;
        this.latestAt = performance.now();
        this.baselines.set(s.tick, s);
        for (const t of this.baselines.keys()) if (t < s.tick - 120) this.baselines.delete(t);
        /*
         * A file missing from a snapshot is gone from this client's view: it left the room, or (in a
         * city, Stage 706) it walked out of this client's interest. Either way its samples go, and
         * the renderer drops its body with them, so nothing is left standing where it was last seen.
         * A file that comes back starts a fresh list: its first sample is where it is now, and with
         * one sample `remoteViews` draws it there rather than lerping from where it was.
         */
        for (const p of s.players) {
          this.names.set(p.id, p.name);
          this.tags.set(p.id, p.tag);
          let list = this.remotes.get(p.id);
          if (!list) this.remotes.set(p.id, (list = []));
          list.push({ tick: s.tick, q: p });
          if (list.length > 40) list.shift();
        }
        for (const id of this.remotes.keys()) if (!s.players.some((p) => p.id === id)) this.remotes.delete(id);
        this.onSnapshot?.(s);
        break;
      }
    }
  }

  /**
   * How many files are in this room, this one included, or 0 with no room (Stage 149). The
   * snapshot's player list is everyone but the recipient, so the room is the remotes plus me — except
   * in a city, which sends only the files near each client and says who is in the room in its roster
   * (Stage 706). A remote the roster has not caught up with yet still counts.
   */
  get files(): number {
    if (this.status !== "joined") return 0;
    const seen = this.remotes.size + 1;
    return this.roster ? Math.max(seen, this.roster.length) : seen;
  }

  /** Interpolated remote players at the current view tick. */
  remoteViews(): RemoteView[] {
    const t = this.serverTickNow() - INTERP_DELAY_TICKS;
    const out: RemoteView[] = [];
    for (const [id, list] of this.remotes) {
      if (list.length === 0) continue;
      let a = list[0]!;
      let b = list[list.length - 1]!;
      for (let i = 0; i < list.length - 1; i++) {
        if (list[i]!.tick <= t && list[i + 1]!.tick >= t) {
          a = list[i]!;
          b = list[i + 1]!;
          break;
        }
      }
      let k = b.tick === a.tick ? 1 : (t - a.tick) / (b.tick - a.tick);
      k = Math.max(0, Math.min(1, k));
      // beyond the newest sample: extrapolate briefly with velocity, then hold
      let x = a.q.x + (b.q.x - a.q.x) * k;
      let y = a.q.y + (b.q.y - a.q.y) * k;
      let z = a.q.z + (b.q.z - a.q.z) * k;
      if (t > b.tick) {
        const dt = Math.min(4, t - b.tick) / SIM_HZ;
        x = b.q.x + b.q.vx * dt;
        y = b.q.y + b.q.vy * dt;
        z = b.q.z + b.q.vz * dt;
      }
      let dy = b.q.yaw - a.q.yaw;
      while (dy > Math.PI) dy -= Math.PI * 2;
      while (dy < -Math.PI) dy += Math.PI * 2;
      out.push({
        id,
        name: this.names.get(id) ?? "BLANK",
        tag: this.tags.get(id) ?? "",
        x, y, z,
        yaw: a.q.yaw + dy * k,
        pitch: a.q.pitch + (b.q.pitch - a.q.pitch) * k,
        height: b.q.height,
        vx: b.q.vx,
        vy: b.q.vy,
        vz: b.q.vz,
        grounded: b.q.grounded,
        slot: b.q.slot,
        alive: b.q.alive,
        stance: numToStance(b.q.stance),
        health: b.q.health,
      });
    }
    return out;
  }
}
